import Constants from "/js/core/constants.js";
import Util from "/js/util/util.js";
import FocusUtil from "/js/util/focus-util.js";
import AppUtil from "/js/util/app-util.js";
import FadeUtil from "/js/util/fade-util.js";
import Metrics from "/js/util/metrics.js";
import Global from "/js/core/global.js";
import Analytics from "/js/util/analytics.js";
import AnimSequencer from "/js/util/anim-sequencer.js";
import UserLessons from "/js/core/user-lessons.js";
import BarView from "/js/top-level-views/bar-view.js";
import StackView from "/js/top-level-views/stack-view.js";
import ServiceUtil from "/js/util/service-util.js";
import CardsHolderViewUtil from "/js/util/cards-holder-view-util";

/** 
 * Is the lesson-card-view container, which has/manages the collection of StackViews, PLUS related
 * UI -- next/previous buttons, progress bar, and lesson title.
 * 
 * All these elements live in `#$inner`, which has its position near the center of `#$el`, which
 * is the full size outer container.
 */
export default class CardsHolderView {

  static #STACK_Y_ABOVE = Metrics.cardHeight * -1.5;
  static #STACK_Y_BELOW = Metrics.cardHeight * +1.5;

  #$el = $('#cardsHolderView');
  #$inner = $('#cardsHolderViewInner');
  #$nextButton = $('#nextButton');
  #$previousButton = $('#previousButton'); 
  #$title = $('#lessonTitle');
  #$stackEls = null;
  
  #stackViews = [];
  #barView;
  
  #stackIndex = 0;
  #numCards = 0;
  #lastCardAnimTime = 0;
  #scale = 1.0;
  #isEnabled;

  constructor() {
    this.#barView = new BarView();
    if (Global.hasCta) {
      this.#$inner.addClass('has-cta'); 
    }
  }

  /**
   * Generates the card stacks using `Global.lessonData` and starts.
   */
  start(alwaysFromBeginning=false) {

    // Title text
    const title = Global.lessonData.meta.title;
    this.#$title.text(title);
    AppUtil.setCssWithoutTransition(this.#$title, () => this.#$title.css('opacity', 0));

    // Card stacks
    this.#stackViews = [];
    this.#$inner.children('.stack').remove();

    Global.lessonData.stacks.forEach(item => {
      const stack = new StackView(item);
      this.#stackViews.push(stack);
      this.#$inner.append(stack.$el);
      this.#numCards += stack.length;
    });
    this.#$stackEls = this.#$inner.find('.stack');

    this.#barView.numCards = AppUtil.getNumCards(Global.lessonData);
    this.#barView.show();

    this.#$el.css("opacity", "0");
    FadeUtil.fadeIn(this.#$el);

    const [stackIndex, cardIndex] = CardsHolderViewUtil.getInitialStackCardIndex();
    this.showStack(stackIndex, cardIndex);

    // Special dev deeplink extra step: Launch interaction if applicable.
    const devInteractionId = Util.getParameterByName("ix");
    if (devInteractionId && (stackIndex > 0 || cardIndex > 0)) {
      this.doNextCard();
    }

    $(document).on("lesson-disable-and-enable-navigation", e => this.#disableAndReenable());

    this.isEnabled = true;
  }

  hide() {
    this.isEnabled = false;
    FadeUtil.fadeOut(this.#$el);
  }

  get $el() {
    return this.#$el;
  }

  get cardIndex() {
    return this.#stackViews[this.#stackIndex].index;
  }

  get currentStack() {
    return this.#stackViews[this.#stackIndex];
  }

  get currentCard() {
    const stack = this.currentStack;
    if (!stack) {
      return null;
    }
    return stack.currentCard;
  }

  get isPastLastStack() {
    return (this.#stackIndex > this.#stackViews.length - 1);
  }

  get scale() {
    return this.#scale;
  }

  /**
   * Calculates the absolute card index of the currently showing card.
   */
  get absoluteCardIndex() {
    let count = 0;
    // iterate the stacks before the current stack
    for (let i = 0; i < this.#stackIndex; i++) {
      count += this.#stackViews[i].length;
    }
    if (this.currentStack) {
      count += this.currentStack.index;
    }
    return count;
  }

  /**
   * Enables or disables all elements re: focusability, accessibility,
   * and all navigation-related event handlers.
   */
  set isEnabled(b) {

    this.#isEnabled = b;

    if (this.#isEnabled) {

      // All handlers must be named, class functions (not anonymous):
      this.#$el.on('card-click', this.#onCardClick);
      this.#$el.on('card-drag-next', this.#onCardDragNext);
      this.#$el.on('card-drag-previous', this.#onCardDragPrevious);

      this.#$nextButton.on('click tap', this.#onNextButtonClick);
      this.#$nextButton.on("keydown", this.#onNextButtonKeydown);
      this.#$nextButton.attr("tabindex", Constants.TABINDEX_LESSON_NEXT_BUTTON);
      Util.setAriaHidden(this.#$nextButton, false);
      this.#$nextButton.attr("aria-role", "button");

      this.#$previousButton.on('click tap', this.#onPreviousButtonClick);
      this.#$previousButton.on("keydown", this.#onPreviousButtonKeydown);
      this.#$previousButton.attr("tabindex", Constants.TABINDEX_LESSON_PREVIOUS_BUTTON);
      Util.setAriaHidden(this.#$previousButton, false);

      $(document).on("keydown", this.#onDocumentKeyDown);
      if (Global.displayMode !== "inline") {
        $(document).on("wheel", this.#onWheel);
      }

    } else {

      this.#$el.off('card-click', this.#onCardClick);
      this.#$el.off('card-drag-next', this.#onCardDragNext);
      this.#$el.off('card-drag-previous', this.#onCardDragPrevious);

      this.#$nextButton.off('click tap', this.#onNextButtonClick);
      this.#$nextButton.off("keydown", this.#onNextButtonKeydown);
      this.#$nextButton.attr("tabindex", -1);
      Util.setAriaHidden(this.#$nextButton, true); // yes this too

      this.#$previousButton.off('click tap', this.#onPreviousButtonClick);
      this.#$previousButton.off("keydown", this.#onPreviousButtonKeydown);
      this.#$previousButton.attr("tabindex", -1);
      Util.setAriaHidden(this.#$previousButton, true);

      $(document).off("keydown", this.#onDocumentKeyDown);
      $(document).off("wheel", this.#onWheel);
    }

    Util.setAriaHidden(this.#$title, !b); // b/c mobile safari

    // Also, make sure current card is made active or not
    // (eg, prevent card from being reachable via Android voiceover when false, for instance)
    const card = this.currentCard;
    if (card) {
      card.isActive = b;
    }
  }

  /** Shows a stack at a given card index. Synchronous. */
  showStack(stackIndex, cardIndex=0) {
    this.#stackIndex = stackIndex;
    
    // Hide other stacks
    const stack = this.#stackViews[this.#stackIndex];
    this.#stackViews.forEach(function(item) {
      (stack === item) ? item.show() : item.hide();
    });
    
    stack.setIndex(cardIndex, true);

    this.#updateUi();
  }

  // Used for debugging
  showLastCard() {
    const i = this.#stackViews.length - 1;
    const j = this.#stackViews[i].cards.length - 1;
    this.showStack(i, j);
  }

  doNextStack(isAlreadyDismissed=false) {
    const outgoingStack = this.#stackViews[this.#stackIndex];
    const lastCard = outgoingStack.cards[outgoingStack.cards.length - 1];

    for (const card of outgoingStack.cards) {
      card.isActive = false;
    }

    const resetTransforms = () => {
      outgoingStack.cards.forEach((card) => { 
        AppUtil.setCssWithoutTransition(card.$el, () => { Util.setTransform(card.$el, ""); });
      });
    };

    this.#stackIndex++;

    this.#updateUi();

    if (isAlreadyDismissed) {
      // Move outgoing stack holder synchronously
      AppUtil.setCssWithoutTransition(
          outgoingStack.$el, 
          () => {
            outgoingStack.hide();
            outgoingStack.$el.css('top', CardsHolderView.#STACK_Y_ABOVE);
          });
      resetTransforms();
    } else {
      // Animate-out outgoing stack holder, which will typically have only one remaining card showing.
      AnimSequencer.runOne(
          outgoingStack.$el, 
          () => {
            outgoingStack.$el.css('top', CardsHolderView.#STACK_Y_ABOVE);
            outgoingStack.hide();
          },
          resetTransforms);
    }

    const interactionId = lastCard.data.interactionId;
    const isLastStack = (this.#stackIndex >= this.#stackViews.length);
    const isLightningLesson = (isLastStack && !interactionId);

    if (interactionId) {
      this.#fadeOutUiElements();
      this.isEnabled = false;
      const f = function() { this.#$el.trigger('interaction-card-animated-out', interactionId); };
      setTimeout($.proxy(f, this), Constants.cardAnimDuration);
    } else if (isLightningLesson) {
      // Rem, a "lightning lesson" is a lesson that has no do-this-now interaction at the end
      cl("lightning lesson")
      this.#fadeOutUiElements();
      this.isEnabled = false;
      const f = function() { this.#$el.trigger('lesson-lesson-done', interactionId); };
      setTimeout($.proxy(f, this), Constants.cardAnimDuration);
    } else {
      this.#animateInIncomingStack();
    }
  }

  #animateInIncomingStack() {
    const incomingStack = this.#stackViews[this.#stackIndex];
    const $incomingStack = incomingStack.$el;
    incomingStack.setIndex(0, true);
    incomingStack.show();

    AppUtil.setCssWithoutTransition($incomingStack, () => {
      $incomingStack.css('top', CardsHolderView.#STACK_Y_BELOW); 
    });
    $incomingStack.css('top', 0);

    UserLessons.current.index = this.absoluteCardIndex;
    UserLessons.saveCurrent();
  }

  doNextStackAfterInteraction() {
    FadeUtil.fadeIn(this.#$el);
    this.#fadeInUiElements();
    this.#animateInIncomingStack();
    this.isEnabled = true;
  }

  doPreviousStack() {
    if (this.#stackIndex <= 0) {
      return;
    }

    const outgoingStack = this.#stackViews[this.#stackIndex];
    this.#stackIndex--;
    const incomingStack = this.#stackViews[this.#stackIndex];
    incomingStack.setIndexToLast(true);
    incomingStack.show();

    const $incomingStack = incomingStack.$el;
    AppUtil.setCssWithoutTransition(
        $incomingStack, () => { $incomingStack.css('top', CardsHolderView.#STACK_Y_ABOVE); });
    $incomingStack.css('top', 0);

    outgoingStack.$el.css('top', CardsHolderView.#STACK_Y_BELOW);
    outgoingStack.hide();
    for (const card of outgoingStack.cards) {
      card.isActive = false;
    }

    this.#updateUi();
  }

  #fadeInUiElements() {
    this.#$title.css("opacity", "1");
    FadeUtil.fadeIn(this.#$nextButton);
    FadeUtil.fadeIn(this.#$previousButton);
    this.#barView.show();
  }

  #fadeOutUiElements() {
    this.#$title.css("opacity", "0");
    FadeUtil.fadeOut(this.#$nextButton);
    FadeUtil.fadeOut(this.#$previousButton);
    this.#barView.hide();
  }

  /**
   * @return false if the action was not taken.
   */
  doNextCard(now=false, isAlreadyDismissed=false) {
    if (!this.currentStack) {
      return false;  // must be at end screen
    }
    const ms = new Date().getTime();
    if (!now) {
      if (ms - this.#lastCardAnimTime < Constants.cardAnimDuration) {
        return false;
      }
    }
    this.#lastCardAnimTime = ms;

    if (this.currentStack.isAtLastCard) {
      this.doNextStack(isAlreadyDismissed);
      if (Global.isLiveMode) {
        ServiceUtil.updateUserLesson(UserLessons.current);
      }
      return true;
    }

    this.currentStack.doNextCard(now, isAlreadyDismissed);
    this.#updateUi();
    UserLessons.current.index = this.absoluteCardIndex;
    UserLessons.saveCurrent();
    return true;
  }

  /**
   * @return false if the action was not taken.
   */
  doPreviousCard() {
    if (!this.currentStack) {
      return false;  // must be at end screen
    }
    if (this.#stackIndex === 0) {
      if (this.currentStack.isAtFirstCard) {
        return false;
      } 
    }
    const ms = new Date().getTime();
    if (ms - this.#lastCardAnimTime < Constants.cardAnimDuration) {
      return false;
    }

    this.#lastCardAnimTime = ms;

    if (this.currentStack.isAtFirstCard) {
      this.doPreviousStack();
    } else {
      this.currentStack.doPreviousCard();
    }
    this.#updateUi();
    return true;
  };

  /**
   * Scales view in relation to the window dimensions.
   */
  resize() {
    if (this.#stackViews.length === 0) {
      return;
    }

    Util.setScale(this.#$inner, Global.cardScale);

    const y = Metrics.calcCardViewY();
    this.#$inner.css("top", y);

    if (Metrics.isMobileWidth) {
      // A little tricky.
      // TODO: Audit all special resize logic thruout app, and see what can be replaced with regular css
      const distanceToTop = y;
      const halfBarHeight = 5 / 2;  // see css
      const halfTopAreaHeight = (8 + 36 + 8) / 2;  // see css
      let barY = -distanceToTop - halfBarHeight + halfTopAreaHeight;
      barY *= 1/ Global.cardScale;
      this.#barView.$el.css("top", barY + "px");
    } else {
      this.#barView.$el.css("top", "");
    }

    // must clear sidebar tab
    const w = (this.#$el.width() / Global.cardScale) - (80 / Global.cardScale);
    this.#$title.width(w);

    this.#updateUi(true);
  }

  #updateUi(now=false) {

    // bar view
    this.#barView.update(this.absoluteCardIndex, now);

    // previous button visibility
    let shouldHide = (this.#stackIndex === 0 && this.cardIndex === 0)
      || (this.#stackIndex >= this.#stackViews.length);
    if (shouldHide) {
      FadeUtil.fadeOut(this.#$previousButton);
    } else {
      FadeUtil.fadeIn(this.#$previousButton);
    } 

    // next button visibility
    shouldHide = (this.#stackIndex >= this.#stackViews.length);
    if (shouldHide) {
      FadeUtil.fadeOut(this.#$nextButton);
    } else {
      FadeUtil.fadeIn(this.#$nextButton);
    }

    // title visibility
    this.#$title.css("opacity", this.absoluteCardIndex > 0 ? 1 : 0);
  }

  #doNextButtonClick() {
    const didNext = this.doNextCard();
    if (didNext) {
      Analytics.trackLessonButtonNext(this.absoluteCardIndex);
    }
  }

  #doPreviousButtonClick() {
    const didPreviousCard = this.doPreviousCard();
    if (didPreviousCard) {
      Analytics.trackLessonButtonPrevious(this.absoluteCardIndex);
    }
  }

  // Note these handlers are defined using arrow function syntax.
  #onCardClick = (e) => {
    if ($(e.target).hasClass('pos0')) {
      Analytics.trackLessonTapNext(this.absoluteCardIndex);
      this.doNextCard();
    }
  };

  #onCardDragNext = (e) => {
    const didNextCard = this.doNextCard();
    if (didNextCard) {
      Analytics.trackLessonSwipeNext(this.absoluteCardIndex);
    }
  };

  #onCardDragPrevious = (e) => {
    const didPreviousCard = this.doPreviousCard();
    if (didPreviousCard) {
      Analytics.trackLessonSwipePrevious(this.absoluteCardIndex);
    }
  };

  #onNextButtonClick = (e) => {
    FocusUtil.blurActiveElement();
    this.#doNextButtonClick();
  };

  #onPreviousButtonClick = (e) => {
    FocusUtil.blurActiveElement();
    this.#doPreviousButtonClick();
  };

  #onNextButtonKeydown = (e) => {
    if (FocusUtil.isEventKeyboardSelect(e)) {
      e.stopImmediatePropagation();
      this.#doNextButtonClick();
    }
  };

  #onPreviousButtonKeydown = (e) => {
    if (FocusUtil.isEventKeyboardSelect(e)) {
      e.stopImmediatePropagation();
      this.#doPreviousButtonClick();
    }
  };

  #onWheel = (e) => {
    if (e.originalEvent.deltaY > 0) {
      const didNextCard = this.doNextCard();
      if (didNextCard) {
        Analytics.trackLessonWheelNext(this.absoluteCardIndex);
      }
    } else {
      const didPreviousCard = this.doPreviousCard();
      if (didPreviousCard) {
        Analytics.trackLessonWheelPrevious(this.absoluteCardIndex);
      }
    }
  };

  #onDocumentKeyDown = (e) => {
    switch (e.key) {
        // Note that different browsers can use different names for special keys:
      case "ArrowRight":
      case "Right":
      case "ArrowDown":
      case "Down":
      case " ":
        const didNextCard = this.doNextCard();
        if (didNextCard) {
          Analytics.trackLessonArrowKeyNext(this.absoluteCardIndex);
        }
        break;
      case "ArrowLeft":
      case "Left":
      case "ArrowUp":
        const didPreviousCard = this.doPreviousCard();
        if (didPreviousCard) {
          Analytics.trackLessonArrowKeyPrevious(this.absoluteCardIndex);
        }
        break;
      case "w":
        if (!Global.isProd) {
          this.showLastCard();
        }
        break;
      case "q":
        if (!Global.isProd) {
          // Skip to end.
          this.#$el.trigger("lesson-lesson-done");
        }
        break;
    }
  };

  #disableAndReenable() {
    if (!this.#isEnabled) {
      return;
    }
    this.isEnabled = false;
    const duration = Constants.cardAnimDuration + 66;
    setTimeout(() => this.isEnabled = true, duration); // yea what
  }
}
